import { PenIcon } from "@xola/icons";
import { Table } from "@xola/ui-kit";
import { DemographicIcon, isEquipmentBased } from "common";
import { produce } from "immer";
import { isInteger, isNil, sortBy } from "lodash";
import React, { useState } from "react";
import {
    DemographicPrice,
    DemographicPricingGroup,
    Experience,
    LineItemCode,
    LineItemTemplate,
    PricingTerm,
    PricingType,
} from "types";
import { isDefaultDemographic } from "../../../models/DemographicUnit";
import { Currency } from "../../Common/Currency";
import { NoTranslate } from "../../Common/NoTranslate";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { getDemographicPriceMap } from "./EditTieredPricingFormHelper";
import { EditTieredPricingModal } from "./EditTieredPricingModal";
import { calculatePriceWithAdjustment, getDemographicPricesForExperienceCatalog } from "@/models/DemographicPrice";
import { updateQuantityInPricingTerms } from "@/models/DemographicPricingTerm";
import { getEquipmentPricesForExperienceCatalog } from "@/models/EquipmentPrice";

interface TieredPricingEditorProps {
    lineItemTemplates: LineItemTemplate[];
    experience: Experience;
    pricingGroup: DemographicPricingGroup;
    onSubmit: (terms: PricingTerm[]) => void;
}

export const TieredPricingEditor = ({
    lineItemTemplates,
    experience,
    pricingGroup,
    onSubmit,
}: TieredPricingEditorProps) => {
    const isEquipment = isEquipmentBased(experience);
    const [showEditTieredPricingModal, setShowEditTieredPricingModal] = useState(false);
    const [selectedTierIndex, setSelectedTierIndex] = useState<number | null>();

    const toggleEditTieredPricingModal = () => {
        setShowEditTieredPricingModal(!showEditTieredPricingModal);
    };

    const handleEditTierModalClose = () => {
        setShowEditTieredPricingModal(false);
        setTimeout(() => {
            setSelectedTierIndex(null);
        }, 500);
    };

    const handleDeleteTier = (index: number) => {
        const currentTerms = [...pricingGroup.terms];
        currentTerms.splice(index, 1);
        onSubmit(currentTerms);
        handleEditTierModalClose();
    };

    const handleEditTierModalSubmit = (tierIndex: number, pricingTerm: PricingTerm) => {
        toggleEditTieredPricingModal();
        if (isInteger(tierIndex)) {
            const currentPricingGroup = { ...pricingGroup };
            const updatedPricingGroup = produce(currentPricingGroup, (draft) => {
                draft.terms[tierIndex] = { ...pricingTerm };
                draft.terms = updateQuantityInPricingTerms(draft.terms);
            });
            onSubmit(updatedPricingGroup.terms);
        }

        setTimeout(() => {
            setSelectedTierIndex(null);
        }, 500);
    };

    return (
        <>
            <Table>
                <Table.Head>
                    <Table.Row>
                        {pricingGroup.pricingType === PricingType.PER_PERSON ? (
                            <Table.Header className="!text-sm">
                                {isEquipment ? "Equipment" : "Demographic"}
                            </Table.Header>
                        ) : null}
                        {pricingGroup.terms.map((term, index) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <Table.Header key={index} className="!text-sm">
                                <span className="flex items-center space-x-1">
                                    <span>
                                        <var data-var="guestCount">{term.quantityMin ?? 1}+</var>{" "}
                                        {isEquipment ? "Equipment" : "Guests"}
                                    </span>
                                    <SecondaryButton
                                        size="tiny"
                                        onClick={() => {
                                            setSelectedTierIndex(index);
                                            toggleEditTieredPricingModal();
                                        }}
                                    >
                                        <PenIcon />
                                    </SecondaryButton>
                                </span>
                            </Table.Header>
                        ))}
                    </Table.Row>
                </Table.Head>
                {pricingGroup.pricingType === PricingType.PER_PERSON ? (
                    <TieredPricingTable experience={experience} pricingTerms={pricingGroup.terms} />
                ) : (
                    <TieredPricingPerOutingTable
                        lineItemTemplates={lineItemTemplates}
                        pricingTerms={pricingGroup.terms}
                    />
                )}
            </Table>
            <EditTieredPricingModal
                isOpen={showEditTieredPricingModal}
                tierIndex={selectedTierIndex!}
                experience={experience}
                lineItemTemplates={lineItemTemplates}
                pricingGroup={pricingGroup}
                onClose={handleEditTierModalClose}
                onDelete={() => handleDeleteTier(selectedTierIndex!)}
                onSubmit={handleEditTierModalSubmit}
            />
        </>
    );
};

interface TieredPricingTableProps {
    pricingTerms: PricingTerm[];
    experience: Experience;
}

const TieredPricingTable = ({ pricingTerms, experience }: TieredPricingTableProps) => {
    const isEquipment = isEquipmentBased(experience);
    // Equipment rows are keyed by `equipmentResource.id` until saved (no template); demographics by template.id.
    const catalogPrices = isEquipment
        ? getEquipmentPricesForExperienceCatalog(experience, pricingTerms[0])
        : getDemographicPricesForExperienceCatalog(experience, pricingTerms[0]);

    const baseDemographicPriceMap: { [key: ID]: number } = getDemographicPriceMap(
        pricingTerms[0]?.demographicPrices ?? [],
    );

    return (
        <Table.Body isStriped={false}>
            {sortBy(catalogPrices, "catalogItem.sequence").map((catalogPrice) => (
                <TieredPricingRow
                    key={catalogPrice.template.id}
                    rowKey={catalogPrice.template.id}
                    catalogPrice={catalogPrice}
                    pricingTerms={pricingTerms}
                    basePrice={baseDemographicPriceMap[catalogPrice.template.id]}
                />
            ))}
        </Table.Body>
    );
};

const TieredPricingRow = ({
    rowKey,
    catalogPrice,
    pricingTerms,
    basePrice,
}: {
    rowKey?: ID;
    catalogPrice: DemographicPrice;
    pricingTerms: PricingTerm[];
    basePrice: number;
}) => {
    const catalogItem = catalogPrice.catalogItem!;
    return (
        <Table.Row>
            <Table.Cell className="w-36">
                <NoTranslate if={!isDefaultDemographic(catalogItem.name)} className="whitespace-normal break-all">
                    <DemographicIcon className="mr-1" name={catalogItem.name} />
                    {catalogItem.name}
                </NoTranslate>
            </Table.Cell>
            {pricingTerms.map((pricingTerm, index) => {
                let price;
                const demographicPrice = pricingTerm.demographicPrices.find((item) => {
                    return item.template.id === rowKey;
                });

                if (demographicPrice) {
                    price =
                        index === 0
                            ? demographicPrice.price
                            : calculatePriceWithAdjustment(basePrice, demographicPrice);
                }

                return (
                    // eslint-disable-next-line react/no-array-index-key
                    <Table.Cell key={index} className="!w-36">
                        {isNil(price) ? null : <Currency>{price}</Currency>}
                    </Table.Cell>
                );
            })}
        </Table.Row>
    );
};

interface TieredPricingPerOutingTableProps {
    lineItemTemplates: LineItemTemplate[];
    pricingTerms: PricingTerm[];
}

const TieredPricingPerOutingTable = ({ lineItemTemplates, pricingTerms }: TieredPricingPerOutingTableProps) => {
    const fixedPriceLineItemTemplate = lineItemTemplates.find((lineItemTemplate) => {
        return lineItemTemplate.code === LineItemCode.PER_OUTING_PRICE;
    });

    const perOutingDemographicPriceItem = pricingTerms[0].demographicPrices.find((demographicPrice) => {
        return fixedPriceLineItemTemplate && demographicPrice.template.id === fixedPriceLineItemTemplate.id;
    });

    const basePrice = perOutingDemographicPriceItem?.price ?? 0;

    return (
        <Table.Body isStriped={false}>
            <Table.Row>
                {pricingTerms.map((pricingTerm, index) => {
                    let price;
                    const demographicPrice = pricingTerm.demographicPrices.find((demographicPrice) => {
                        return (
                            fixedPriceLineItemTemplate && demographicPrice.template.id === fixedPriceLineItemTemplate.id
                        );
                    });

                    if (demographicPrice) {
                        price = index === 0 ? basePrice : calculatePriceWithAdjustment(basePrice, demographicPrice);
                    }

                    return (
                        // eslint-disable-next-line react/no-array-index-key
                        <Table.Cell key={index} className="!w-36">
                            {isNil(price) ? "-" : <Currency>{price}</Currency>}
                        </Table.Cell>
                    );
                })}
            </Table.Row>
        </Table.Body>
    );
};
